import * as Sentry from '@sentry/react';

import { addThread } from 'bundles/discussions/actions/ThreadSettingsActions';
import { follow as followApi, unfollow as unfollowApi } from 'bundles/discussions/api/followApi';
import {
  getAnswersWithComments,
  getComments,
  getQuestion,
  savePost as savePostApi,
} from 'bundles/discussions/api/threadDetailApi';
import { cancelUpvote as cancelUpvoteApi, upvote as upvoteApi } from 'bundles/discussions/api/upvoteApi';
import { answers, loadingStates, savingStates } from 'bundles/discussions/constants';
import unreadTracker from 'bundles/discussions/utils/unreadTracker';
import { loadCourseMaterials } from 'bundles/in-course-stores/actions/CourseMaterialsActions';
import { refreshCourseViewGrade } from 'bundles/in-course-stores/actions/CourseViewGradeActions';
import { getProgress } from 'bundles/in-course-stores/actions/ProgressActions';
import type { NaptimeError } from 'bundles/naptimejs';

const ANSWER_LIMIT = answers.limitPerPage;
export const fetchThread = function (
  actionContext: $TSFixMe,
  { questionId, contextId, userId, forumType, sort, page }: $TSFixMe
) {
  // Only fetch if it's not already present.
  // If it's already present, add that thread to the thread settings
  const threadDetail =
    actionContext.getStore('ThreadDetailsStore').getQuestion(questionId) &&
    actionContext.getStore('ThreadDetailsStore').getQuestion(questionId);
  const threadsPage = actionContext.getStore('ThreadsStore').getThreadsPage();
  const threadFromThreadsStore = threadsPage.find((q: $TSFixMe) => q.questionId === questionId);
  if (!threadDetail || unreadTracker.hasUnread(threadFromThreadsStore)) {
    const setLoadingState = function (loadingState: $TSFixMe) {
      actionContext.dispatch('QUESTION_LOADING_STATE', {
        questionId,
        loadingState,
      });
    };
    setLoadingState(loadingStates.LOADING);
    actionContext.dispatch('UNSET_QUESTION', { questionId });
    getQuestion({ questionId, userId, contextId, forumType, sort, page })
      .then((resp) => {
        actionContext.dispatch('LOAD_QUESTION', {
          response: resp,
          questionId,
          forumType,
        });
        setLoadingState(loadingStates.DONE);
        actionContext.executeAction(addThread, Object.assign(resp.elements[0], { forumType }));
        unreadTracker.markRead(questionId);
      })
      .catch(() => setLoadingState(loadingStates.ERROR));
  } else {
    actionContext.executeAction(addThread, actionContext.getStore('ThreadDetailsStore').getQuestion(questionId));
    unreadTracker.markRead(questionId);
  }
};
/*
 * This is an internal function that does not set the loading state of the comment to be LOADING before loading the
 * comment page, because if this is called multiple times, batching the `dispatch` calls into one call is much more
 * efficient. Any wrapper on this function must set the comment loading state to be LOADING before calling it.
 */
const fetchCommentPagesHelper = function (
  actionContext: $TSFixMe,
  {
    userId,
    questionId,
    answerId,
    userForumAnswerId,
    forumType,
    includeDeleted,
    startPage,
    endPage,
    commentLimit,
  }: $TSFixMe
) {
  return getComments({
    userForumAnswerId,
    forumType,
    includeDeleted,
    startPage,
    endPage,
    commentLimit,
  })
    .then((response) =>
      actionContext.dispatch('LOAD_COMMENT_PAGES', {
        response,
        userId,
        userForumAnswerId,
        answerId,
        startPage,
        questionId,
      })
    )
    .catch(() => {
      actionContext.dispatch('COMMENT_LOADING_STATE', {
        answerId,
        loadingState: loadingStates.ERROR,
      });
    });
};
export const fetchMultipleCommentPages = function (
  actionContext: $TSFixMe,
  {
    userId,
    questionId,
    answerId,
    userForumAnswerId,
    forumType,
    includeDeleted,
    startPage,
    endPage,
    commentLimit,
  }: $TSFixMe
) {
  actionContext.dispatch('COMMENT_LOADING_STATE', {
    answerId,
    loadingState: loadingStates.LOADING,
  });
  actionContext.executeAction(fetchCommentPagesHelper, {
    userId,
    questionId,
    answerId,
    userForumAnswerId,
    forumType,
    includeDeleted,
    startPage,
    endPage,
    commentLimit,
  });
};
export const fetchAnswers = function (
  actionContext: $TSFixMe,
  {
    questionId,
    userId,
    contextId,
    courseId,
    courseSlug,
    sort,
    page,
    limit,
    forumType,
    includeDeleted,
    forumTypeSetting,
  }: $TSFixMe
) {
  const setLoadingState = function (loadingState: $TSFixMe) {
    actionContext.dispatch('ANSWER_LOADING_STATE', {
      questionId,
      sort,
      page,
      loadingState,
    });
  };
  setLoadingState(loadingStates.LOADING);
  getAnswersWithComments({
    questionId,
    userId,
    contextId,
    forumType,
    sort,
    page,
    limit: limit || ANSWER_LIMIT,
    includeDeleted,
    forumTypeSetting,
  })
    .then((resp) => {
      actionContext.dispatch('LOAD_ANSWERS_WITH_COMMENTS', {
        userId,
        response: resp,
        questionId,
        page,
        sort,
        forumType,
      });
    })
    .then(() => {
      // update course progress so that when completing discussion items, course home progress indicators update
      // oddly enough, this seems to be the commit point for many discussion operations
      // not clear if course context always exists (e.g. authoring views), so boolean check for condition where it doesn't
      if (courseId && courseSlug) {
        actionContext.executeAction(refreshCourseViewGrade, { courseId });
        actionContext.executeAction(getProgress, { courseId, refreshProgress: true });
        actionContext.executeAction(loadCourseMaterials, { courseSlug, refetch: true });
      }
    })
    .catch(() => setLoadingState(loadingStates.ERROR));
};
/**
 * Isolated the logic for how to determine the storage ID to simplfy testing.
 */
export const getFluxiblePostId = (options: $TSFixMe, parentPost: $TSFixMe) => {
  const { parentForumAnswerId } = options.data;
  return parentForumAnswerId || parentPost.id;
};
export const savePost = (
  actionContext: $TSFixMe,
  { options, question, forumType, parentPost, isFollowing, onSuccess }: $TSFixMe
) => {
  const targetId = getFluxiblePostId(options, parentPost);
  actionContext.dispatch('SET_SAVING_STATE', {
    savingState: savingStates.SAVING,
    id: targetId,
  });
  if (question) {
    if (isFollowing) {
      actionContext.dispatch('FOLLOW', { question });
    } else {
      actionContext.dispatch('UNFOLLOW', { question });
    }
  }
  savePostApi(options, forumType)
    .then((resp) => {
      unreadTracker.markRead(parentPost.questionId);
      actionContext.dispatch('RECEIVE_POST', {
        resp,
        data: options.data,
        parentPost,
        forumType,
      });
      actionContext.dispatch('SET_SAVING_STATE', {
        savingState: savingStates.SAVED,
        id: targetId,
      });
      onSuccess?.();
    })
    .catch((error: NaptimeError | undefined) => {
      let currentSavingState;
      const errorCodeToSavingState: Record<string, string> = {
        FORUM_POST_INVALID_TERM_EXCEPTION: savingStates.INVALID_TERM_ERROR,
        FORUM_POST_RATE_LIMIT_REACHED_EXCEPTION: savingStates.RATE_LIMIT_ERROR,
        FORUM_POST_USER_BLOCKED_EXCEPTION: savingStates.USER_BLOCKED_ERROR,
      };
      if (typeof error === 'object' && error !== null) {
        const errorCode: string = error?.responseJSON?.errorCode;
        currentSavingState = errorCodeToSavingState[errorCode] ?? savingStates.ERROR;
      } else {
        currentSavingState = savingStates.ERROR;
        Sentry.captureException(error, {
          extra: { message: 'Unexpected error recieved while attempting to save forum post' },
        });
      }

      actionContext.dispatch('SET_SAVING_STATE', {
        savingState: currentSavingState,
        id: targetId,
        retry: options.data,
      });
    });
};
export const upvote = (actionContext: $TSFixMe, { post }: $TSFixMe) => {
  upvoteApi(post)
    .then(() => actionContext.dispatch('UPVOTE', { post }))
    .catch(() => actionContext.dispatch('UPVOTE_ERROR', { post }));
};
export const cancelUpvote = (actionContext: $TSFixMe, { post }: $TSFixMe) => {
  cancelUpvoteApi(post)
    .then(() => actionContext.dispatch('CANCEL_UPVOTE', { post }))
    .catch(() => actionContext.dispatch('UPVOTE_ERROR', { post }));
};
export const follow = (actionContext: $TSFixMe, { question }: $TSFixMe) => {
  followApi(question)
    .then(() => actionContext.dispatch('FOLLOW', { question }))
    .catch(() => actionContext.dispatch('FOLLOW_ERROR', { question }));
};
export const unfollow = (actionContext: $TSFixMe, { question }: $TSFixMe) => {
  unfollowApi(question)
    .then(() => actionContext.dispatch('UNFOLLOW', { question }))
    .catch(() => actionContext.dispatch('FOLLOW_ERROR', { question }));
};
